import { useEffect, useMemo, useState } from "react";
import api, { API } from "../lib/api";
import { getToken } from "../lib/tokenStorage";
import { toast } from "sonner";
import { FileText, Plus, X, Trash2, ExternalLink, Copy, Download, User, Eye, ImageIcon, CheckCircle2, Archive } from "lucide-react";
import DeleteConfirmDialog from "../components/DeleteConfirmDialog";
import BulkActionBar from "../components/BulkActionBar";
import { useAuth } from "../lib/auth";
const FRRO_URL = "https://indianfrro.gov.in/frro/FormC";

// Section headers for the FRRO copy-to-clipboard dump (A-J of Form C).
const SECTIONS = [
  ["A. Personal", ["surname", "given_names", "gender", "date_of_birth", "nationality", "special_category"]],
  ["B. Permanent Address", ["home_address", "home_city", "home_state", "home_postal_code", "home_country"]],
  ["C. Contact", ["mobile_home_country", "whatsapp_number", "email", "emergency_contact_name", "emergency_contact_relationship", "emergency_contact_number"]],
  ["D. Passport", ["passport_no", "passport_issue_date", "passport_expiry_date", "passport_place_of_issue"]],
  ["E. Visa", ["visa_number", "visa_type", "visa_subtype", "visa_issue_date", "visa_expiry_date", "visa_place_of_issue"]],
  ["F. Travel", ["flight_number", "arrival_airport", "arrival_city", "arrival_date", "arrival_time", "country_of_departure", "city_of_departure"]],
  ["G. Accommodation", ["checkin_date", "checkin_time", "expected_checkout_date", "total_nights", "intended_duration_days"]],
  ["H. Visit", ["purpose_of_visit", "employed_in_india"]],
  ["I. Next Destination", ["next_destination_address", "next_destination_city", "next_destination_state", "next_destination_country"]],
  ["J. In-India Contact", ["india_mobile_number", "local_contact_number"]],
];
const _pretty = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const _buildClipboard = (r) => {
  const lines = [`FRRO Form C — ${r.participant_name || r.given_names || "(no name)"}`, ""];
  for (const [title, keys] of SECTIONS) {
    lines.push(title);
    for (const k of keys) {
      const v = r[k];
      if (v !== undefined && v !== null && String(v).trim() !== "") lines.push(`  ${_pretty(k)}: ${v}`);
    }
    lines.push("");
  }
  return lines.join("\n");
};

// Detect if a row came in via the participant portal wizard (i.e. was
// actually submitted with pictures + full data — not just the auto-created
// blank stub row).
const _isSubmitted = (r) =>
  r.source === "participant_portal" ||
  Boolean(r.submitted_at) ||
  Boolean(r.passport_pic_url || r.passport_photo_url) ||
  Boolean(r.visa_pic_url || r.visa_copy_url);

export default function CForms() {
  const { user } = useAuth();
  const role = user?.real_role || user?.effective_role;
  // GEHM can create + fill C-Forms (their bread and butter) but NEVER delete —
  // audit trail must remain intact. Founder/BDOM keep the trash icon.
  // Delete restricted to Founder across every core collection.
  const canDelete = role === "FOUNDER";
  const [rows, setRows] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ participant_name:"", arrival_date:new Date().toISOString().slice(0,10), filed_date:"" });
  const [activeTab, setActiveTab] = useState("all");   // "all" | "submitted"
  const [detailFor, setDetailFor] = useState(null);    // row → opens detail modal
  const [bulkDate, setBulkDate] = useState(new Date().toISOString().slice(0, 10));
  const [bulkBusy, setBulkBusy] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const toggleSel = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const load = async () => {
    const [c, p] = await Promise.all([
      api.get("/cforms"),
      api.get("/program-bookings").catch(()=>({data:[]})),
    ]);
    const sorted = [...c.data].sort((a, b) => (a.arrival_date || "").localeCompare(b.arrival_date || ""));
    setRows(sorted);
    setParticipants(p.data);
    api.post("/cforms/check-overdue").catch(()=>{});
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.participant_name) return toast.error("Name required");
    await api.post("/cforms", form);
    setForm({ participant_name:"", arrival_date:new Date().toISOString().slice(0,10), filed_date:"" });
    setOpen(false); await load(); toast.success("Recorded");
  };

  const patchRow = async (r, patch) => {
    await api.patch(`/cforms/${r.id}`, patch);
    await load();
  };
  const del = async (id) => { await api.delete(`/cforms/${id}`); await load(); };
  const [delTarget, setDelTarget] = useState(null);

  // Bulk-delete — Founder-only. Snapshots every row to a single Recycle-Bin
  // bundle so a mis-click is one-tap restorable.
  const bulkDelete = async () => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    await api.post("/cforms/bulk-delete", { ids });
    setSelected(new Set());
    await load();
  };
  const toggleSelectVisible = (visibleRows) => {
    const visibleIds = visibleRows.map(r => r.id);
    const allSelected = visibleIds.every(id => selected.has(id));
    setSelected(prev => {
      const next = new Set(prev);
      if (allSelected) visibleIds.forEach(id => next.delete(id));
      else visibleIds.forEach(id => next.add(id));
      return next;
    });
  };

  const isOverdue = (r) => {
    if (r.status === "completed" || r.filed_date) return false;
    if (!r.arrival_date) return false;
    return Date.now() - new Date(r.arrival_date).getTime() > 24 * 3600 * 1000;
  };
  const statusOf = (r) => (r.status === "completed" || r.filed_date) ? "Submitted" : "Due";

  const submittedRows = useMemo(() => rows.filter(_isSubmitted), [rows]);
  const visibleRows = activeTab === "submitted" ? submittedRows : rows;

  // Pending rows arriving on the picked bulk date — feeds the two toolbar
  // actions (Zip download + Mark all filed).
  const bulkPendingCount = useMemo(() => {
    if (!bulkDate) return 0;
    return rows.filter(r =>
      (r.arrival_date || "").slice(0, 10) === bulkDate &&
      !(r.status === "completed" || r.filed_date)
    ).length;
  }, [rows, bulkDate]);

  const markAllFiled = async () => {
    if (!bulkDate) { toast.error("Pick a date first"); return; }
    if (!window.confirm(`Mark every pending C-Form for arrivals on ${bulkDate} as filed on FRRO?`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/cforms/mark-filed-bulk", { date: bulkDate });
      toast.success(`Marked ${data.marked || 0} C-form${data.marked === 1 ? "" : "s"} as filed`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk mark failed");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2"><FileText className="text-brand"/><h1 className="text-2xl font-semibold">C-Form Data</h1></div>
          <p className="text-sm text-[color:var(--aii-text-2)] mt-1">Every India-bound participant submits their C-Form data from their portal — it lands here for GEHM Jaipur to file with FRRO. Tap the <b>File on FRRO</b> button on any row to open the government portal and post the record.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a href={`${API}/cforms/export.csv?status=pending&auth=${getToken()}`}
             data-testid="cf-export-csv"
             download
             className="text-sm px-3 py-2 border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50 font-semibold flex items-center gap-1.5"
             title="Download all pending C-Forms as a CSV formatted for FRRO bulk import">
            <Download size={14}/> Export CSV (FRRO)
          </a>
          <a href={FRRO_URL} target="_blank" rel="noopener noreferrer"
             data-testid="cf-frro-top"
             className="text-sm px-3 py-2 border border-brand text-brand rounded-md hover:bg-[color:var(--aii-green-tint)] font-semibold flex items-center gap-1.5">
            <ExternalLink size={14}/> Open FRRO Form C portal
          </a>
          <button data-testid="cf-new" onClick={()=>setOpen(true)} className="text-sm px-3 py-2 bg-brand text-white rounded-md hover:bg-brand-dark font-semibold flex items-center gap-1.5"><Plus size={14}/> Manual entry</button>
        </div>
      </div>

      {/* --------- Tabs --------- */}
      <div className="flex items-center gap-2 border-b border-[color:var(--aii-border)]">
        {[
          { k: "all", label: "All Records", count: rows.length },
          { k: "submitted", label: "Data Submitted from Portal", count: submittedRows.length },
        ].map(t => (
          <button
            key={t.k}
            data-testid={`cf-tab-${t.k}`}
            onClick={() => setActiveTab(t.k)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
              activeTab === t.k
                ? "border-brand text-brand"
                : "border-transparent text-[color:var(--aii-text-2)] hover:text-[color:var(--aii-text-1)]"
            }`}>
            {t.label}
            <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
              activeTab === t.k ? "bg-[color:var(--aii-green-tint)] text-brand" : "bg-slate-100 text-slate-600"
            }`}>{t.count}</span>
          </button>
        ))}
      </div>

      {/* --------- FRRO Session toolbar — day-scoped bulk actions --------- */}
      <div className="aii-card p-4 flex flex-wrap items-center gap-3" data-testid="cf-frro-session-bar">
        <div className="flex-1 min-w-[220px]">
          <div className="text-[10px] uppercase tracking-widest text-brand font-bold">FRRO Filing Session</div>
          <div className="text-xs text-[color:var(--aii-muted)]">Pick an arrival date → download every uploaded passport + visa image as a zip, then bulk-mark those rows filed once the FRRO portal session wraps.</div>
        </div>
        <label className="flex items-center gap-2 text-xs text-[color:var(--aii-text-2)]">
          Arrival date
          <input
            type="date" value={bulkDate}
            onChange={e => setBulkDate(e.target.value)}
            className="input-aii !py-1.5 !px-2"
            data-testid="cf-bulk-date"/>
        </label>
        <span className="text-xs text-[color:var(--aii-muted)]" data-testid="cf-bulk-pending-count">
          <b className="text-[color:var(--aii-text-1)]">{bulkPendingCount}</b> pending
        </span>
        <a
          href={bulkDate ? `${API}/cforms/download-zip?date=${bulkDate}&auth=${getToken()}` : "#"}
          onClick={(e) => { if (!bulkDate) { e.preventDefault(); toast.error("Pick a date first"); } }}
          download
          data-testid="cf-bulk-zip"
          className="text-sm px-3 py-2 border border-brand text-brand rounded-md hover:bg-[color:var(--aii-green-tint)] font-semibold flex items-center gap-1.5">
          <Archive size={14}/> Download images ZIP
        </a>
        <button
          onClick={markAllFiled}
          disabled={bulkBusy || bulkPendingCount === 0}
          data-testid="cf-bulk-mark-filed"
          className="text-sm px-3 py-2 bg-brand text-white rounded-md hover:bg-brand-dark font-semibold flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed">
          <CheckCircle2 size={14}/> {bulkBusy ? "Marking…" : `Mark all filed (${bulkPendingCount})`}
        </button>
      </div>

      {open && (
        <div className="aii-card p-5">
          <div className="flex justify-between mb-3"><h3 className="font-semibold">Manual C-Form</h3><button onClick={()=>setOpen(false)}><X size={16}/></button></div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">Participant</label>
              <input value={form.participant_name} onChange={e=>setForm(v=>({...v, participant_name:e.target.value}))}
                list="cf-participants" className="input-aii" placeholder="Type or pick from bookings" />
              <datalist id="cf-participants">
                {participants.map(p=><option key={p.id} value={p.customer}/>)}
              </datalist>
            </div>
            <div><label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">Arrival date</label><input type="date" value={form.arrival_date} onChange={e=>setForm(v=>({...v,arrival_date:e.target.value}))} className="input-aii"/></div>
            <div><label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">Filed date (optional)</label><input type="date" value={form.filed_date} onChange={e=>setForm(v=>({...v,filed_date:e.target.value}))} className="input-aii"/></div>
          </div>
          <div className="flex justify-end mt-4"><button onClick={add} className="bg-brand text-white font-semibold px-5 py-2 rounded-md hover:bg-brand-dark">Save</button></div>
        </div>
      )}

      {/* --------- Submitted-from-Portal card view --------- */}
      {activeTab === "submitted" ? (
        <div>
          {submittedRows.length === 0 ? (
            <div className="aii-card p-10 text-center text-[color:var(--aii-muted)] italic">No participants have submitted their C-form from the portal yet.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cf-submitted-grid">
              {submittedRows.map(r => (
                <SubmittedCard key={r.id} r={r} onOpen={() => setDetailFor(r)} isOverdue={isOverdue(r)} />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="aii-card overflow-x-auto">
          {canDelete && (
            <BulkActionBar
              count={selected.size}
              label="C-Form"
              onClear={() => setSelected(new Set())}
              onDelete={bulkDelete}
            />
          )}
          <div className="overflow-x-auto">
            <table className="aii-table">
              <thead><tr>
                {canDelete && (
                  <th className="w-8">
                    <input type="checkbox" data-testid="cf-sel-all"
                      checked={visibleRows.length > 0 && visibleRows.every(r => selected.has(r.id))}
                      onChange={() => toggleSelectVisible(visibleRows)}/>
                  </th>
                )}
                <th>Partner</th><th>Destination</th><th>City</th><th>Participant</th>
                <th>Gender</th><th>DOB</th><th>Programme</th><th>Duration</th>
                <th>Start</th><th>End</th>
                <th>Arrival date</th><th>Filed date</th><th>Status</th><th></th>
              </tr></thead>
              <tbody>
                {visibleRows.length===0 && <tr><td colSpan={canDelete ? 15 : 14} className="text-center py-10 text-[color:var(--aii-muted)]">No program bookings yet — add a booking first and C-forms will appear automatically here.</td></tr>}
                {visibleRows.map(r=>{
                  const overdue = isOverdue(r);
                  const status = statusOf(r);
                  const submitted = _isSubmitted(r);
                  return (
                    <tr key={r.id} data-testid={`cf-row-${r.id}`} className={selected.has(r.id) ? "bg-[color:var(--aii-green-tint)]/40" : ""}>
                      {canDelete && (
                        <td><input type="checkbox" data-testid={`cf-sel-${r.id}`} checked={selected.has(r.id)} onChange={() => toggleSel(r.id)}/></td>
                      )}
                      <td className="font-medium text-brand text-xs">{r.partner_name || <span className="italic text-[color:var(--aii-muted)] font-normal">—</span>}</td>
                      <td className="text-xs whitespace-nowrap">{r.destination || "—"}</td>
                      <td className="text-xs whitespace-nowrap">{r.city || "—"}</td>
                      <td className="font-medium">
                        {r.participant_name || <span className="italic text-[color:var(--aii-muted)] font-normal">— no name yet —</span>}
                        {submitted && <span className="ml-1.5 inline-block text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-[color:var(--aii-green-tint)] text-brand rounded font-bold" title="Submitted from participant portal with pictures">Submitted</span>}
                        {(r.filed_by || r.filed_at) && (
                          <div className="text-[10px] text-[color:var(--aii-muted)] mt-0.5 flex items-center gap-1" data-testid={`cf-filed-audit-${r.id}`}>
                            <CheckCircle2 size={10} className="text-emerald-600 flex-shrink-0"/>
                            <span>
                              Filed{r.filed_by ? <> by <b className="text-[color:var(--aii-text-1)] font-semibold">{r.filed_by}</b></> : ""}
                              {r.filed_at ? <> · {new Date(r.filed_at).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</> : (r.filed_date ? <> · {r.filed_date}</> : null)}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="text-xs">{r.gender || "—"}</td>
                      <td className="text-xs whitespace-nowrap">{(r.dob || r.date_of_birth || "").slice(0, 10) || "—"}</td>
                      <td className="text-xs">{r.programme || "—"}</td>
                      <td className="text-xs whitespace-nowrap">{r.duration || "—"}</td>
                      <td className="text-xs whitespace-nowrap">{(r.start_date || "").slice(0, 10) || "—"}</td>
                      <td className="text-xs whitespace-nowrap">{(r.end_date || "").slice(0, 10) || "—"}</td>
                      <td>
                        <span data-testid={`cf-arr-${r.id}`} className="text-sm font-medium text-[color:var(--aii-text)]">
                          {(r.arrival_date || "").slice(0, 10) || "—"}
                        </span>
                      </td>
                      <td>
                        <input data-testid={`cf-filed-${r.id}`} type="date" defaultValue={(r.filed_date||"").slice(0,10)}
                          onBlur={e=>patchRow(r, { filed_date: e.target.value, status: e.target.value ? "completed" : "pending" })}
                          className="px-2 py-1 border border-[color:var(--aii-border)] rounded bg-white text-sm" />
                      </td>
                      <td>
                        <select data-testid={`cf-status-${r.id}`} value={status === "Submitted" ? "completed" : "pending"}
                          onChange={e => patchRow(r, { status: e.target.value })}
                          className={`px-2 py-1 border rounded bg-white text-sm font-semibold ${
                            status === "Submitted" ? "border-emerald-500 text-emerald-700" :
                            overdue ? "border-red-500 text-red-700" : "border-amber-500 text-amber-700"
                          }`}>
                          <option value="pending">{overdue ? "Due (Overdue >24h)" : "Due"}</option>
                          <option value="completed">Submitted</option>
                        </select>
                      </td>
                      <td className="text-right">
                        <div className="inline-flex items-center gap-1">
                          {submitted && (
                            <button
                              data-testid={`cf-view-${r.id}`}
                              title="View all submitted data + downloadable pictures"
                              onClick={() => setDetailFor(r)}
                              className="text-[10px] px-2 py-1 border border-brand text-brand rounded hover:bg-[color:var(--aii-green-tint)] font-semibold inline-flex items-center gap-1">
                              <Eye size={11}/> View
                            </button>
                          )}
                          <button
                            data-testid={`cf-copy-${r.id}`}
                            title="Copy every field of this C-Form as text — paste directly into the FRRO government portal"
                            onClick={async () => {
                              const txt = _buildClipboard(r);
                              try { await navigator.clipboard.writeText(txt); toast.success("Copied for FRRO — paste in the government form"); }
                              catch { toast.error("Copy failed — please select + copy manually"); }
                            }}
                            className="text-[10px] px-2 py-1 border border-slate-300 text-slate-700 rounded hover:bg-slate-50 font-semibold inline-flex items-center gap-1">
                            <Copy size={11}/> Copy for FRRO
                          </button>
                          <a href={FRRO_URL} target="_blank" rel="noopener noreferrer"
                             data-testid={`cf-frro-${r.id}`}
                             title="Open the FRRO Form C portal in a new tab to file this record"
                             className="text-[10px] px-2 py-1 bg-brand text-white rounded hover:bg-brand-dark font-semibold inline-flex items-center gap-1">
                            <ExternalLink size={11}/> File on FRRO
                          </a>
                          {canDelete && (
                            <button data-testid={`cf-del-${r.id}`} onClick={()=>setDelTarget(r)} className="text-[color:var(--aii-muted)] hover:text-red-600"><Trash2 size={14}/></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {detailFor && (
        <SubmittedDetailModal r={detailFor} onClose={() => setDetailFor(null)} />
      )}
      <DeleteConfirmDialog
        open={!!delTarget}
        title="Delete this C-Form?"
        description="The FRRO record will be removed. Restore any time from the Recycle Bin (Founder-only)."
        label={delTarget ? `${delTarget.customer || "Unknown"} · ${delTarget.passport_no || ""}` : ""}
        onCancel={() => setDelTarget(null)}
        onConfirm={async () => { await del(delTarget.id); setDelTarget(null); }}
      />
    </div>
  );
}


// ---------------------------------------------------------------------------
// SubmittedCard — one participant that filled the wizard from their portal.
// Shows a photo (self_pic > passport_pic thumb), name, destination, arrival
// date and a big "View" button that opens the detail modal.
// ---------------------------------------------------------------------------
function SubmittedCard({ r, onOpen, isOverdue }) {
  const photo = r.self_pic_url || r.passport_pic_url || r.passport_photo_url || "";
  const status = r.status === "completed" || r.filed_date ? "Filed on FRRO" : (isOverdue ? "Overdue >24h" : "Awaiting filing");
  const statusCls = r.status === "completed" || r.filed_date
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : isOverdue
      ? "bg-red-50 text-red-700 border-red-200"
      : "bg-amber-50 text-amber-800 border-amber-200";
  return (
    <button
      data-testid={`cf-submitted-card-${r.id}`}
      onClick={onOpen}
      className="aii-card p-4 text-left hover:border-brand hover:shadow transition-all">
      <div className="flex items-start gap-3">
        {photo ? (
          <img src={photo} alt={r.participant_name || "participant"} className="w-14 h-14 rounded-full object-cover border border-[color:var(--aii-border)]"/>
        ) : (
          <div className="w-14 h-14 rounded-full bg-[color:var(--aii-green-tint)] text-brand font-bold text-xl flex items-center justify-center border border-[color:var(--aii-border)]">
            <User size={22}/>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-[color:var(--aii-text-1)] truncate">{r.participant_name || "(unnamed)"}</div>
          <div className="text-xs text-[color:var(--aii-muted)] truncate">
            {r.partner_name || "—"} · {r.destination || "—"}{r.city ? ` / ${r.city}` : ""}
          </div>
          <div className="text-[11px] text-[color:var(--aii-muted)] mt-1">
            Arrival <b className="text-[color:var(--aii-text-1)]">{(r.arrival_date || "—").slice(0, 10)}</b>
            {r.submitted_at && <> · Submitted {r.submitted_at.slice(0, 10)}</>}
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between">
        <span className={`text-[10px] uppercase tracking-widest px-2 py-1 border rounded font-bold ${statusCls}`}>{status}</span>
        <span className="text-xs text-brand font-semibold inline-flex items-center gap-1"><Eye size={12}/> View details</span>
      </div>
    </button>
  );
}


// ---------------------------------------------------------------------------
// SubmittedDetailModal — full 10-section view of a participant's submitted
// C-form. Every uploaded document is a downloadable link, and a single big
// "Copy for FRRO" button pipes everything into the clipboard.
// ---------------------------------------------------------------------------
function SubmittedDetailModal({ r, onClose }) {
  const files = [
    { label: "Passport Photo Page", url: r.passport_pic_url || r.passport_photo_url },
    { label: "Visa Copy", url: r.visa_pic_url || r.visa_copy_url },
    { label: "Flight Ticket", url: r.flight_ticket_url },
    { label: "Participant Photo", url: r.self_pic_url },
  ].filter(f => f.url);
  const copy = async () => {
    const txt = _buildClipboard(r);
    try { await navigator.clipboard.writeText(txt); toast.success("Copied for FRRO — paste in the government form"); }
    catch { toast.error("Copy failed — please select + copy manually"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="cf-detail-modal">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl my-8" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-5 border-b border-[color:var(--aii-border)] flex items-start justify-between gap-3 sticky top-0 bg-white rounded-t-lg z-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-brand font-bold">Submitted from participant portal</div>
            <h3 className="font-display text-xl font-semibold">{r.participant_name || "(unnamed)"}</h3>
            <div className="text-xs text-[color:var(--aii-muted)] mt-0.5">
              {r.partner_name || "—"} · {r.destination || "—"}{r.city ? ` / ${r.city}` : ""} · Arrival {(r.arrival_date || "—").slice(0, 10)}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={copy} data-testid="cf-detail-copy" className="text-sm px-3 py-2 bg-brand text-white rounded-md hover:bg-brand-dark font-semibold flex items-center gap-1.5">
              <Copy size={14}/> Copy for FRRO
            </button>
            <a href={FRRO_URL} target="_blank" rel="noopener noreferrer"
               data-testid="cf-detail-frro"
               className="text-sm px-3 py-2 border border-brand text-brand rounded-md hover:bg-[color:var(--aii-green-tint)] font-semibold flex items-center gap-1.5">
              <ExternalLink size={14}/> File on FRRO
            </a>
            <button onClick={onClose} className="p-2 text-[color:var(--aii-muted)] hover:text-red-600" data-testid="cf-detail-close"><X size={16}/></button>
          </div>
        </div>
        {/* Body */}
        <div className="p-5 space-y-5">
          {/* Uploaded documents */}
          {files.length > 0 && (
            <section>
              <div className="text-[10px] uppercase tracking-widest text-brand font-bold mb-2">Uploaded Documents</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="cf-detail-files">
                {files.map((f) => (
                  <a
                    key={f.label}
                    href={f.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    data-testid={`cf-detail-file-${f.label.replace(/\s+/g,'-').toLowerCase()}`}
                    className="border border-[color:var(--aii-border)] rounded-md p-2 hover:border-brand hover:bg-[color:var(--aii-green-tint)] transition flex flex-col items-center text-center gap-1.5 group">
                    <div className="w-full aspect-square rounded overflow-hidden bg-slate-100 flex items-center justify-center">
                      {/\.(jpe?g|png|webp|gif|heic|heif)$/i.test(f.url) || f.url.includes("/api/public/files/") ? (
                        <img src={f.url} alt={f.label} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.replaceWith(Object.assign(document.createElement('div'), { className: 'text-slate-400', textContent: '📄' })); }}/>
                      ) : (
                        <ImageIcon size={30} className="text-slate-400"/>
                      )}
                    </div>
                    <div className="text-[10px] text-[color:var(--aii-text-2)] group-hover:text-brand font-semibold">{f.label}</div>
                    <div className="text-[9px] text-[color:var(--aii-muted)] inline-flex items-center gap-0.5"><Download size={9}/> Download</div>
                  </a>
                ))}
              </div>
            </section>
          )}
          {/* Data sections */}
          {SECTIONS.map(([title, keys]) => {
            const rowsInSection = keys.map(k => [k, r[k]]).filter(([, v]) => v !== undefined && v !== null && String(v).trim() !== "");
            if (rowsInSection.length === 0) return null;
            return (
              <section key={title}>
                <div className="text-[10px] uppercase tracking-widest text-brand font-bold mb-2">{title}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                  {rowsInSection.map(([k, v]) => (
                    <div key={k} className="flex items-start gap-2 border-b border-slate-100 py-1">
                      <div className="text-[color:var(--aii-muted)] text-xs w-40 flex-shrink-0">{_pretty(k)}</div>
                      <div className="font-medium text-[color:var(--aii-text-1)] break-words">{String(v)}</div>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}
